import React, { useState } from "react";
import { X, Bell, CheckCircle2 } from "lucide-react";
import { api, priceFmt } from "../lib/api";

/**
 * PriceAlertModal — collect email + target price for a deal, POST to backend.
 * Props: deal (obj), onClose
 */
export default function PriceAlertModal({ deal, onClose }) {
  const currentPrice = deal?.price_current || 0;
  const defaultTarget = currentPrice > 0
    ? Math.max(1, Math.floor(currentPrice * 0.9))
    : "";

  const [email, setEmail] = useState("");
  const [target, setTarget] = useState(defaultTarget);
  const [consent, setConsent] = useState(true); // opt-in to newsletter (checked by default, user can uncheck)
  const [state, setState] = useState({ loading: false, ok: false, error: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (state.loading) return;
    const t = parseFloat(target);
    if (!t || t <= 0) {
      setState({ loading: false, ok: false, error: "Inserisci un prezzo valido." });
      return;
    }
    if (currentPrice > 0 && t >= currentPrice) {
      setState({
        loading: false,
        ok: false,
        error: `Il prezzo target deve essere inferiore al prezzo attuale (${priceFmt(currentPrice)}).`,
      });
      return;
    }
    setState({ loading: true, ok: false, error: "" });
    try {
      await api.post(`/deals/${deal.id}/price-alert`, {
        email: email.trim().toLowerCase(),
        target_price: t,
        marketing_consent: consent,
      });
      setState({ loading: false, ok: true, error: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Non è stato possibile creare l'alert. Riprova.";
      setState({ loading: false, ok: false, error: msg });
    }
  };

  return (
    <div
      data-testid="price-alert-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl bg-zinc-950 border border-zinc-800 p-6"
      >
        <button
          onClick={onClose}
          data-testid="price-alert-close"
          aria-label="Chiudi"
          className="absolute right-4 top-4 text-zinc-500 hover:text-white"
        >
          <X size={20} />
        </button>

        {state.ok ? (
          <div data-testid="price-alert-success" className="text-center py-4">
            <CheckCircle2 className="mx-auto text-[#FBD51C] mb-3" size={48} />
            <h3 className="font-display font-black text-xl text-white mb-2">
              Alert attivato!
            </h3>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Riceverai un&apos;email a{" "}
              <span className="text-white font-bold">{email}</span> non appena
              il prezzo scenderà a {priceFmt(parseFloat(target))} o meno.
            </p>
            <button
              onClick={onClose}
              data-testid="price-alert-done"
              className="mt-6 bg-[#FBD51C] hover:bg-[#F5C518] text-black font-black text-sm uppercase tracking-wider px-6 py-3 rounded-lg"
            >
              OK
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3">
              <Bell size={18} className="text-[#FBD51C]" />
              <div className="font-mono-bdd text-[10px] uppercase tracking-[0.3em] text-[#FBD51C]">
                Avviso prezzo
              </div>
            </div>
            <h3 className="font-display font-black text-2xl text-white mb-2 leading-tight">
              Avvisami quando scende
            </h3>
            <p className="text-sm text-zinc-500 leading-relaxed mb-5">
              Prezzo attuale:{" "}
              <span className="text-white font-bold">
                {priceFmt(currentPrice)}
              </span>
              . Ti manderemo un&apos;email non appena il prezzo raggiunge la tua
              soglia.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs font-mono-bdd uppercase tracking-widest text-zinc-500 mb-1.5">
                  La tua email
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tua@email.com"
                  data-testid="price-alert-email-input"
                  className="w-full bg-black/40 border border-zinc-800 focus:border-[#FBD51C] outline-none rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600"
                />
              </div>
              <div>
                <label className="block text-xs font-mono-bdd uppercase tracking-widest text-zinc-500 mb-1.5">
                  Avvisami se scende sotto (€)
                </label>
                <input
                  type="number"
                  required
                  min="1"
                  step="0.01"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  data-testid="price-alert-target-input"
                  className="w-full bg-black/40 border border-zinc-800 focus:border-[#FBD51C] outline-none rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600"
                />
              </div>

              <label className="flex items-start gap-2 text-[11px] text-zinc-500 leading-relaxed cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="price-alert-consent"
                  className="mt-0.5 accent-[#FBD51C]"
                />
                <span>
                  Iscrivimi anche alla newsletter settimanale con le migliori
                  offerte. Puoi disiscriverti in ogni momento.
                </span>
              </label>

              {state.error && (
                <div
                  data-testid="price-alert-error"
                  className="text-xs text-rose-400"
                >
                  {state.error}
                </div>
              )}

              <button
                type="submit"
                disabled={state.loading}
                data-testid="price-alert-submit"
                className="w-full bg-[#FBD51C] hover:bg-[#F5C518] disabled:bg-zinc-800 disabled:text-zinc-500 text-black font-black text-sm uppercase tracking-wider px-5 py-3 rounded-lg transition-colors"
              >
                {state.loading ? "Attivo…" : "Attiva l'avviso prezzo"}
              </button>
              <p className="text-[10px] text-zinc-600 text-center">
                Gratuito. Nessuna registrazione, nessuna password.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
